import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const features = [
  {
    icon: "ri-server-line",
    title: "VMware & Nutanix Alternative",
    description:
      "Arcfra AECP (Arcfra Enterprise Cloud Platform) is a full-stack HCI software platform that replaces VMware vSphere and Nutanix AHV — with no vendor lock-in, no per-socket royalties, and full enterprise-grade capabilities built in. Recognized by Gartner as a representative VMware alternative in 2025.",
  },
  {
    icon: "ri-cpu-line",
    title: "AVE — Arcfra Virtualisation Engine",
    description:
      "AVE (Arcfra Virtualisation Engine) converges compute, storage, and networking into a single software-defined platform — eliminating the complexity of traditional three-tier architectures. Supports 600+ enterprise customers worldwide, deployable from as few as 3 nodes.",
  },
  {
    icon: "ri-hard-drive-2-line",
    title: "ABS — Arcfra Block Storage",
    description:
      "ABS (Arcfra Block Storage) delivers high-performance block storage for databases, virtual machines, and AI workloads — with built-in disaster recovery (RPO=0, RTO in minutes), iSCSI/NFS/vHost protocols, and no separate storage license required.",
  },
  {
    icon: "ri-shield-keyhole-line",
    title: "Ransomware-Resilient Backup",
    description:
      "ExaGrid's time-locked delayed deletes and Synology's immutable snapshots ensure your backup copies cannot be encrypted or deleted by ransomware — maintaining a clean recovery point no matter what.",
  },
  {
    icon: "ri-cloud-line",
    title: "Hybrid Cloud Integration",
    description:
      "Synology C2 and ExaGrid cloud tiering enable seamless offsite replication to AWS, Azure, or Google Cloud — building a cost-effective hybrid architecture without sacrificing on-premise recovery speed.",
  },
  {
    icon: "ri-clockwise-line",
    title: "Instant VM Recovery",
    description:
      "ExaGrid Instant Recovery for VMs (IR-VM) brings virtual machines back online in minutes, not hours — minimising RTO and ensuring business continuity during unexpected outages or ransomware attacks.",
  },
];

const arcfraVsTable = [
  { feature: "Platform", arcfra: "AECP — full-stack HCI software", vmware: "ESXi + vSAN (Broadcom)", nutanix: "AHV + AOS" },
  { feature: "Licensing Model", arcfra: "Subscription, by CPU core", vmware: "Per-socket + vSAN add-on", nutanix: "Per-node subscription" },
  { feature: "Vendor Lock-in", arcfra: "None — open standards", vmware: "High (proprietary)", nutanix: "Medium (AHV)" },
  { feature: "Virtualisation", arcfra: "AVE — 600+ enterprise customers", vmware: "ESXi (Broadcom pricing)", nutanix: "AHV (native)" },
  { feature: "Block Storage", arcfra: "ABS — no separate license", vmware: "vSAN — extra license", nutanix: "Included" },
  { feature: "Kubernetes", arcfra: "AKE — CNCF conformant, built-in", vmware: "Tanzu — separate product", nutanix: "NKE add-on" },
  { feature: "File Storage", arcfra: "AFS — no extra hardware cost", vmware: "3rd-party NAS required", nutanix: "Files add-on" },
  { feature: "AI Workloads", arcfra: "Neutree — private MaaS platform", vmware: "No native AI orchestration", nutanix: "No native AI orchestration" },
  { feature: "Disaster Recovery", arcfra: "RPO=0, RTO in minutes (ABS)", vmware: "SRM — extra license", nutanix: "Leap — extra license" },
  { feature: "Analyst Recognition", arcfra: "Gartner ×2 2025 + DCIG Top 5", vmware: "Legacy incumbent", nutanix: "Leader (higher price)" },
  { feature: "Malaysian Support", arcfra: "Local SE Distribution", vmware: "Regional only", nutanix: "Regional only" },
  { feature: "TCO", arcfra: "Significantly lower vs VMware", vmware: "Baseline (300–500% increase)", nutanix: "10–20% lower" },
];

const migrationSteps = [
  {
    step: "01",
    icon: "ri-search-eye-line",
    title: "Infrastructure Assessment",
    subtitle: "Week 1–2",
    description:
      "Our certified engineers audit your existing VMware or Nutanix environment — cataloguing workloads, VM counts, storage usage, and licensing spend. We identify consolidation opportunities and produce a detailed TCO comparison report.",
    bullets: [
      "On-site or remote assessment available",
      "VMware vSphere & Nutanix AHV inventory scan",
      "Licensing cost analysis vs Arcfra",
      "Risk & dependency mapping",
    ],
    badge: "Free Assessment",
    badgeIcon: "ri-gift-line",
    spotlightAnchor: "/arcfra-spotlight#step-01",
    spotlightLabel: "See step detail on Arcfra Spotlight",
    assessmentLink: "/arcfra-assessment",
    assessmentLabel: "Book Free Assessment",
  },
  {
    step: "02",
    icon: "ri-settings-3-line",
    title: "Pilot Deployment & Validation",
    subtitle: "Week 3–6",
    description:
      "We deploy an Arcfra AECP pilot cluster alongside your existing infrastructure. Non-critical workloads are migrated first so your team can validate performance, compatibility, and operational processes — with zero production risk.",
    bullets: [
      "AECP pilot cluster setup (AVE + ABS + ANS)",
      "Live VM migration (P2V / V2V) via AVE",
      "ExaGrid backup integration & testing",
      "Runbook and DR validation",
    ],
    badge: "Zero Downtime",
    badgeIcon: "ri-checkbox-circle-line",
    spotlightAnchor: "/arcfra-spotlight#step-03",
    spotlightLabel: "See step detail on Arcfra Spotlight",
    assessmentLink: null,
    assessmentLabel: null,
  },
  {
    step: "03",
    icon: "ri-rocket-line",
    title: "Full Cutover & Ongoing Support",
    subtitle: "Week 7+",
    description:
      "Production workloads are migrated in rolling batches with rollback safeguards at every stage. Once fully cut over, SE Distribution provides local post-migration support, firmware updates, and proactive health monitoring.",
    bullets: [
      "Rolling production cutover plan",
      "Arcfra & ExaGrid training for IT team",
      "Local SE support (KL-based engineers)",
      "Annual health-check programme",
    ],
    badge: "Local Support",
    badgeIcon: "ri-customer-service-2-line",
    spotlightAnchor: "/arcfra-spotlight#step-04",
    spotlightLabel: "See step detail on Arcfra Spotlight",
    assessmentLink: null,
    assessmentLabel: null,
  },
];

const industries = [
  { icon: "ri-bank-line", name: "Financial Services" },
  { icon: "ri-hospital-line", name: "Healthcare" },
  { icon: "ri-government-line", name: "Government" },
  { icon: "ri-store-line", name: "Retail Chains" },
  { icon: "ri-building-2-line", name: "Enterprise IT" },
  { icon: "ri-cpu-line", name: "Technology & SaaS" },
];

const stats = [
  { value: "1,000+", label: "Enterprise Customers (AECP)" },
  { value: "600+", label: "AVE Virtualisation Customers" },
  { value: "Gartner", label: "Recognized VMware Alternative 2025" },
  { value: "3", label: "Authorized Storage Brands" },
];

const dataProtectionStructuredData = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.sedistribution.com.my/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Solutions",
        "item": "https://www.sedistribution.com.my/solutions"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "HCI & Storage Infrastructure",
        "item": "https://www.sedistribution.com.my/solutions/data-protection"
      }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "HCI & Storage Infrastructure Malaysia | Arcfra VMware Alternative, ExaGrid Backup | SE Distribution",
    "description": "Replace VMware & Nutanix with Arcfra AECP HCI — up to 60% lower TCO, Gartner-recognized VMware alternative 2025. ExaGrid ransomware-proof backup storage, Synology NAS. Authorized distributor in Malaysia. Free migration assessment available.",
    "url": "https://www.sedistribution.com.my/solutions/data-protection",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sedistribution.com.my/" },
        { "@type": "ListItem", "position": 2, "name": "Solutions", "item": "https://www.sedistribution.com.my/solutions" },
        { "@type": "ListItem", "position": 3, "name": "HCI & Storage Infrastructure", "item": "https://www.sedistribution.com.my/solutions/data-protection" }
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "SE Distribution",
      "url": "https://www.sedistribution.com.my"
    }
  }
];

export default function DataProtectionPage() {
  useSEO({
    title: "HCI & Storage Infrastructure Malaysia | Arcfra VMware Alternative, ExaGrid Backup | SE Distribution",
    description: "Replace VMware & Nutanix with Arcfra HCI — up to 60% lower TCO. ExaGrid ransomware-proof backup, Synology NAS. Authorized distributor in Malaysia. Free migration assessment available.",
    canonical: "https://www.sedistribution.com.my/solutions/data-protection",
    keywords: "VMware alternative Malaysia, Arcfra HCI Malaysia, ExaGrid backup Malaysia, hyperconverged infrastructure Malaysia, Nutanix alternative, enterprise backup Malaysia, data protection Malaysia",
    structuredData: dataProtectionStructuredData,
  });
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[480px] md:h-[580px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20enterprise%20data%20center%20server%20racks%20rows%20glowing%20LED%20lights%20cold%20aisle%20containment%20dramatic%20cinematic%20perspective%20hyperconverged%20infrastructure%2C%20professional%20photography%2C%20wide%20angle&width=1400&height=700&seq=hci-hero&orientation=landscape"
          alt="HCI & Storage Infrastructure"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-20 max-w-7xl mx-auto w-full">
          <nav className="flex items-center gap-2 text-xs text-white/50 mb-6">
            <Link to="/" className="hover:text-white/80 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/solutions" className="hover:text-white/80 transition-colors">Solutions</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/80">HCI & Storage Infrastructure</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4 w-fit">
            <i className="ri-exchange-line text-white/70 text-xs"></i>
            <span className="text-xs font-semibold text-white/80 uppercase tracking-widest">VMware & Nutanix Alternative</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-2xl">
            Hyperconverged &amp; Storage Infrastructure
          </h1>
          <p className="text-lg text-white/75 max-w-xl leading-relaxed mb-8">
            Replace VMware and Nutanix with Arcfra AECP — a full-stack HCI software platform (AVE, ABS, AKE, ANS, AFS) recognised by Gartner in 2025. Backed by ExaGrid and Synology for complete data protection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/arcfra-spotlight"
              className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 w-fit"
            >
              Explore Arcfra Platform <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/contact"
              className="border border-white/50 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer w-fit"
            >
              Request a Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1a1a1a] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-xl md:text-2xl font-bold text-white mb-1">{s.value}</p>
              <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Spotlight Banners */}
      <section className="py-12 px-6 lg:px-20 bg-[#f0ede8]">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* Arcfra Banner */}
          <div className="flex flex-col lg:flex-row items-center gap-8 bg-white rounded-xl p-8 border border-[#1a1a1a]/10">
            <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
              <i className="ri-server-line text-white text-2xl"></i>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#1a1a1a]/5 rounded-full px-3 py-1 mb-2">
                <i className="ri-star-fill text-[#1a1a1a] text-xs"></i>
                <span className="text-xs font-semibold text-[#1a1a1a] uppercase tracking-widest">Featured Platform</span>
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-1">Arcfra — The VMware &amp; Nutanix Alternative for Malaysian Enterprises</h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-2xl">
                Arcfra AECP is a full-stack HCI software platform built on AVE (virtualisation), ABS (block storage), AKE (Kubernetes), ANS (networking), and AFS (file storage) — recognised by Gartner as a VMware alternative in 2025, with 1,000+ enterprise customers worldwide. Significantly lower TCO vs VMware, no vendor lock-in, and full local support through SE Distribution.
              </p>
            </div>
            <Link
              to="/arcfra-spotlight"
              className="flex-shrink-0 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              View Arcfra Spotlight <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          {/* ExaGrid Banner */}
          <div className="flex flex-col lg:flex-row items-center gap-8 bg-white rounded-xl p-8 border border-[#1a1a1a]/10">
            <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
              <i className="ri-hard-drive-2-line text-white text-2xl"></i>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#1a1a1a]/5 rounded-full px-3 py-1 mb-2">
                <i className="ri-star-fill text-[#1a1a1a] text-xs"></i>
                <span className="text-xs font-semibold text-[#1a1a1a] uppercase tracking-widest">Featured Product</span>
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-1">ExaGrid — Fastest Backup Recovery &amp; Ransomware-Proof Storage</h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-2xl">
                ExaGrid is the only backup storage with a landing zone for instant recovery, scale-out architecture for no forklift upgrades, and a non-network-facing repository that stops ransomware from encrypting your backups.
              </p>
              <div className="flex flex-wrap gap-3 mt-4 justify-center lg:justify-start">
                <Link
                  to="/exagrid-spotlight"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1a1a1a] hover:underline cursor-pointer"
                >
                  <i className="ri-external-link-line"></i> ExaGrid Spotlight
                </Link>
                <span className="text-gray-300 text-xs">|</span>
                <Link
                  to="/exagrid-backup-malaysia"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1a1a1a] hover:underline cursor-pointer"
                >
                  <i className="ri-shield-keyhole-line"></i> ExaGrid Backup Malaysia — Sizing &amp; Pricing
                </Link>
              </div>
            </div>
            <Link
              to="/exagrid-backup-malaysia"
              className="flex-shrink-0 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              Get a Sizing Quote <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 px-6 lg:px-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Overview</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight mb-5">
              Escape VMware Licensing. Modernise Your Infrastructure.
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-4">
              With Broadcom's acquisition of VMware driving licensing costs up by 300–500% for many Malaysian enterprises, organisations are urgently seeking alternatives. Arcfra delivers a production-ready hyperconverged infrastructure platform that matches VMware and Nutanix capabilities — at a dramatically lower total cost of ownership.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              SE Distribution pairs Arcfra AECP with ExaGrid's purpose-built backup appliances and Synology's enterprise NAS to create a complete, layered infrastructure stack — covering compute virtualisation (AVE), block storage (ABS), Kubernetes (AKE), networking (ANS), file storage (AFS), backup, and disaster recovery in a single integrated architecture.
            </p>
            <div className="flex flex-wrap gap-3">
              {["Arcfra", "ExaGrid", "Synology"].map((b) => (
                <span key={b} className="text-xs font-semibold bg-[#f5f3ee] text-[#1a1a1a] px-4 py-1.5 rounded-full">
                  {b}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="w-full h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=IT%20engineer%20working%20in%20server%20room%20data%20center%20maintaining%20hyperconverged%20infrastructure%20racks%20enterprise%2C%20dramatic%20lighting%2C%20high%20detail%2C%20professional%20photography%2C%20realistic&width=700&height=500&seq=hci-overview&orientation=landscape"
                alt="Data center engineer"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-lg px-5 py-4 border border-gray-100 w-52">
              <p className="text-xs text-gray-400 mb-1">Authorized Brands</p>
              <span className="text-sm font-bold text-[#1a1a1a]">Arcfra · ExaGrid · Synology</span>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="bg-[#f8f7f4] py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Comparison</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">Arcfra vs VMware vs Nutanix</h2>
            <p className="text-gray-500 text-base mt-3 max-w-xl mx-auto">See why Malaysian enterprises are choosing Arcfra as their hyperconverged infrastructure platform.</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full bg-white text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider w-1/4">Feature</th>
                  <th className="px-6 py-4 text-center">
                    <span className="inline-flex items-center gap-1.5 bg-[#1a1a1a] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      <i className="ri-star-fill text-xs"></i> Arcfra
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500">VMware</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500">Nutanix</th>
                </tr>
              </thead>
              <tbody>
                {arcfraVsTable.map((row, i) => (
                  <tr key={row.feature} className={i % 2 === 0 ? "bg-white" : "bg-[#fafaf9]"}>
                    <td className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{row.feature}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-semibold text-[#1a1a1a]">{row.arcfra}</span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">{row.vmware}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">{row.nutanix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Capabilities</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">What Our Solutions Deliver</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-[#f8f7f4] rounded-lg p-7 border border-transparent hover:border-[#1a1a1a]/10 transition-colors">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                  <i className={`${f.icon} text-xl text-[#1a1a1a]`}></i>
                </div>
                <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Migration Journey */}
      <section className="bg-[#f8f7f4] py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-3">
              Your Migration Path — Start to Finish
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              From initial assessment to full production cutover, SE Distribution manages every stage of your VMware or Nutanix migration to Arcfra.
            </p>
          </div>

          {/* Step cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            {migrationSteps.map((s, idx) => (
              <div key={s.step} className="relative bg-white rounded-xl p-8 border border-gray-100 flex flex-col hover:border-[#1a1a1a]/20 transition-colors">
                {/* Connector line (hidden on last) */}
                {idx < migrationSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-[52px] right-0 translate-x-1/2 w-6 h-0.5 bg-gray-200 z-10" />
                )}
                {/* Step number */}
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-4xl font-black text-[#1a1a1a]/8 leading-none select-none">{s.step}</span>
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0">
                    <i className={`${s.icon} text-white text-lg`}></i>
                  </div>
                </div>
                {/* Badge */}
                <div className="inline-flex items-center gap-1.5 bg-[#f0ede8] rounded-full px-3 py-1 mb-3 w-fit">
                  <i className={`${s.badgeIcon} text-[#1a1a1a] text-xs`}></i>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#1a1a1a]">{s.badge}</span>
                </div>
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-1">{s.title}</h3>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{s.subtitle}</p>
                <p className="text-sm text-gray-500 leading-relaxed mb-5">{s.description}</p>
                <ul className="space-y-2 mb-6">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-[#1a1a1a] mt-0.5 flex-shrink-0"></i>
                      {b}
                    </li>
                  ))}
                </ul>
                {/* Footer links */}
                <div className="mt-auto pt-5 border-t border-gray-100 flex flex-col gap-2">
                  {s.assessmentLink && (
                    <Link
                      to={s.assessmentLink}
                      className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-xs font-semibold px-4 py-2.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer w-fit"
                    >
                      <i className="ri-calendar-check-line text-sm"></i>
                      {s.assessmentLabel}
                    </Link>
                  )}
                  <Link
                    to={s.spotlightAnchor}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-[#1a1a1a] transition-colors group cursor-pointer"
                  >
                    <i className="ri-external-link-line text-xs"></i>
                    <span className="group-hover:underline">{s.spotlightLabel}</span>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Timeline bar */}
          <div className="bg-white rounded-xl border border-gray-100 px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg">
                <i className="ri-calendar-check-line text-[#1a1a1a] text-base"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-[#1a1a1a]">Typical end-to-end timeline: 6–10 weeks</p>
                <p className="text-xs text-gray-400">Varies by workload complexity and cluster size</p>
              </div>
            </div>
            <Link
              to="/arcfra-assessment"
              className="bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 flex-shrink-0"
            >
              Book Free Assessment <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-[#1a1a1a] py-16 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3 block">Industries Served</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white">Who We Serve</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {industries.map((ind) => (
              <div key={ind.name} className="flex flex-col items-center gap-2 text-center py-5 px-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${ind.icon} text-2xl text-white/70`}></i>
                </div>
                <p className="text-xs text-white/60">{ind.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
            Ready to Replace VMware or Nutanix?
          </h2>
          <p className="text-gray-500 text-base mb-8 max-w-xl mx-auto">
            Our infrastructure specialists will assess your current VMware or Nutanix environment and design a migration path to Arcfra — with zero disruption and measurable cost savings from day one.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#1a1a1a] text-white text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
            >
              Talk With Us <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/arcfra-spotlight"
              className="border border-[#1a1a1a] text-[#1a1a1a] text-sm font-medium px-8 py-3.5 rounded-md hover:bg-[#f8f7f4] transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Arcfra Platform
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
